/**
 * ThyroidTwinPage — iter621 — 9-ти близнак.
 * Manual log + 30/90/180-day TSH trend + йод/селен meters + AI insight (mix tone).
 */
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, Save, FileText } from "lucide-react";
import { toast } from "sonner";
import CortisolThyroidAxisCard from "@/components/dashboard/CortisolThyroidAxisCard";
import ProfileContextStrip from "@/components/shared/ProfileContextStrip";

import { MobileTopBar } from "@/components/MobileTopBar";
const TREND_OPTIONS = [
  { id: 30,  label: "30 дни" },
  { id: 90,  label: "90 дни" },
  { id: 180, label: "180 дни" },
];

function tshColor(tsh) {
  if (tsh == null) return "#94a3b8";
  if (tsh < 0.4) return "#F97316";   // suppressed
  if (tsh < 1.0) return "#FBBF24";   // slightly low
  if (tsh <= 2.5) return "#10B981";  // optimal
  if (tsh <= 4.0) return "#FBBF24";  // upper-normal
  if (tsh <= 10.0) return "#F97316"; // subclinical
  return "#EF4444";                  // overt
}

function compositeColor(score) {
  if (score == null) return "#94a3b8";
  if (score >= 80) return "#10B981";
  if (score >= 60) return "#FBBF24";
  if (score >= 40) return "#F97316";
  return "#EF4444";
}

function Sparkline({ points, color, isDark }) {
  // Tiny inline SVG sparkline for TSH trend
  if (!points || points.length === 0) return null;
  const vals = points.map((p) => p.tsh_miu_l).filter((v) => v != null);
  if (vals.length < 2) {
    return (
      <p className={`text-xs italic ${isDark ? "text-slate-400" : "text-slate-500"}`}>
        Нужни са поне 2 измервания за тенденция.
      </p>
    );
  }
  const W = 600, H = 80, P = 6;
  const min = Math.min(...vals, 0);
  const max = Math.max(...vals, 5);
  const span = max - min || 1;
  const step = (W - 2 * P) / (vals.length - 1);
  const yOf = (v) => H - P - ((v - min) / span) * (H - 2 * P);
  const path = vals.map((v, i) => `${i === 0 ? "M" : "L"} ${P + i * step} ${yOf(v)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-20" preserveAspectRatio="none" data-testid="thyroid-tsh-sparkline">
      {/* Reference band: 1.0-2.5 optimal */}
      <rect x={P} y={yOf(2.5)} width={W - 2 * P} height={yOf(1.0) - yOf(2.5)}
            fill={isDark ? "rgba(16,185,129,0.10)" : "rgba(16,185,129,0.15)"} />
      <path d={path} stroke={color} fill="none" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {vals.map((v, i) => (
        <circle key={i} cx={P + i * step} cy={yOf(v)} r="3" fill={tshColor(v)} />
      ))}
    </svg>
  );
}

function NutrientMeter({ label, value, target, unit, isDark }) {
  const pct = value == null ? 0 : Math.min(150, Math.round((value / target) * 100));
  const color = value == null ? "#94a3b8"
              : pct < 50  ? "#EF4444"
              : pct < 80  ? "#F97316"
              : pct <= 110 ? "#10B981"
              : pct <= 130 ? "#FBBF24"
              : "#F97316";
  const status_bg = value == null ? "няма данни"
                  : pct < 50  ? "недостатъчно"
                  : pct < 80  ? "ниско"
                  : pct <= 110 ? "оптимално"
                  : pct <= 130 ? "леко над цел"
                  : "над препоръчителното";
  return (
    <div className={`p-3 rounded-xl border ${isDark ? "bg-slate-900/40 border-slate-800" : "bg-white border-slate-200"}`}
         data-testid={`thyroid-nutrient-${label.toLowerCase()}`}>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-xs font-semibold opacity-80">{label}</span>
        <span className="text-[10px] opacity-60">цел {target} {unit}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold tabular-nums" style={{ color }}>
          {value == null ? "—" : value}
        </span>
        <span className="text-xs opacity-60">{unit}/ден</span>
      </div>
      <div className={`h-1.5 rounded-full mt-2 ${isDark ? "bg-slate-800" : "bg-slate-100"}`}>
        <div className="h-full rounded-full transition-all duration-500"
             style={{ width: `${Math.min(100, pct)}%`, backgroundColor: color }} />
      </div>
      <div className="text-[10px] mt-1 opacity-70">{status_bg}</div>
    </div>
  );
}

export default function ThyroidTwinPage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const [snap, setSnap] = useState(null);
  const [insight, setInsight] = useState("");
  const [loading, setLoading] = useState(true);
  const [trendDays, setTrendDays] = useState(90);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    tsh_miu_l: "",
    ft4_pmol_l: "",
    ft3_pmol_l: "",
    anti_tpo_iu_ml: "",
    anti_tg_iu_ml: "",
    note_bg: "",
  });

  const fetchSnap = async () => {
    try {
      const { data } = await axios.get(`${API}/thyroid-twin/snapshot`, { timeout: 15000 });
      setSnap(data);
    } catch {
      // silent — empty state will render
    }
  };

  const fetchInsight = async () => {
    try {
      const { data } = await axios.post(`${API}/thyroid-twin/insights`, {}, { timeout: 30000 });
      if (data?.snapshot) setSnap(data.snapshot);
      setInsight(data?.insight_bg || "");
    } catch { /* silent */ }
  };

  const fetchHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/thyroid-twin/history?days=${trendDays}`, { timeout: 15000 });
      // Sort ASC by measured_on for sparkline (API returns DESC)
      const sorted = (data?.labs || []).slice().sort((a, b) => (a.measured_on || "").localeCompare(b.measured_on || ""));
      setHistory(sorted);
    } catch { /* silent */ }
  };

  useEffect(() => {
    // eslint-disable-next-line
    (async () => {
      setLoading(true);
      // Snapshot + history block the loading state (fast endpoints)
      await Promise.all([fetchSnap(), fetchHistory()]);
      setLoading(false);
      // Insight runs in the background — LLM can take 20s+
      fetchInsight();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API]);

  // eslint-disable-next-line
  useEffect(() => { fetchHistory(); /* eslint-disable-next-line */ }, [trendDays]);

  const save = async () => {
    const payload = {};
    for (const k of ["tsh_miu_l", "ft4_pmol_l", "ft3_pmol_l", "anti_tpo_iu_ml", "anti_tg_iu_ml"]) {
      const v = form[k];
      if (v !== "" && v != null && !Number.isNaN(parseFloat(v))) payload[k] = parseFloat(v);
    }
    if (Object.keys(payload).length === 0) {
      toast.error("Въведи поне една стойност.");
      return;
    }
    if (form.note_bg) payload.note_bg = form.note_bg;
    setSaving(true);
    try {
      await axios.post(`${API}/thyroid-twin/lab`, payload);
      toast.success("Записахме лабораторния резултат.");
      setForm({ tsh_miu_l: "", ft4_pmol_l: "", ft3_pmol_l: "", anti_tpo_iu_ml: "", anti_tg_iu_ml: "", note_bg: "" });
      await Promise.all([fetchSnap(), fetchInsight(), fetchHistory()]);
      // iter623 — Notify other twins (PassiveStressBanner, dashboard tile) to re-fetch.
      // The Cortisol-Thyroid axis depends on TSH; UnifiedHealthTwinCard caches its
      // snapshot. Both should react to new lab entries.
      window.dispatchEvent(new Event("thyroid-lab-changed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при записа.");
    } finally {
      setSaving(false);
    }
  };

  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const composite = snap?.composite;
  const labs = snap?.latest_labs || {};
  const bands = snap?.bands || {};
  const nutr = snap?.nutrients_7d || {};

  const trendPoints = useMemo(
    () => history.filter((h) => h.tsh_miu_l != null).map((h) => ({
      tsh_miu_l: h.tsh_miu_l, measured_on: h.measured_on,
    })),
    [history]
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="thyroid-twin-page">
      <MobileTopBar title="Щитовидна" fallbackTo="/dashboard" testid="thyroid-twin-mobile-topbar" />
        <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`} data-testid="thyroid-twin-page">
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        {/* iter1017 — профилът захранва близнака */}
        <ProfileContextStrip domain="thyroid" isDark={isDark} className="mb-4" />
        <button onClick={() => navigate("/dashboard")} className="text-xs opacity-60 hover:opacity-100 mb-3 flex items-center gap-1" data-testid="back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Към таблото
        </button>

        <div className="flex items-center gap-2 mb-4">
          <span className="text-2xl" aria-hidden="true">🟢</span>
          <h1 className="text-xl font-bold">Thyroid Twin · щитовидна жлеза</h1>
        </div>

        {/* iter1046 — TSH сам не казва етажа: двойката живее в ендокринните оси */}
        <button
          onClick={() => navigate("/dashboard/hormonal-twin")}
          data-testid="thyroid-to-endocrine-axes"
          className="mb-4 w-full text-left text-[11px] px-3 py-2 rounded-lg bg-violet-500/10 hover:bg-violet-500/20 transition-colors"
        >
          <strong>Ендокринни оси — къде е повредата →</strong> TSH сам не
          различава жлезата от хипофизата. Двойката TSH + свободен T4 казва
          етажа.
        </button>

        {/* iter622 — Cortisol-Thyroid Axis circuit-level coaching (shown when detected) */}
        {snap?.axis_check?.detected && (
          <div className="mb-4">
            <CortisolThyroidAxisCard axis={snap.axis_check} defaultCollapsed={false} />
          </div>
        )}

        {/* Composite + bands */}
        <div className={`p-5 rounded-2xl border mb-4 ${cardCls}`} data-testid="thyroid-summary">
          <div className="text-[10px] uppercase opacity-50 mb-1">Thyroid Twin Score</div>
          <div className="flex items-baseline gap-2 mb-3">
            <div className="text-5xl font-bold tabular-nums"
                 style={{ color: compositeColor(composite) }}
                 data-testid="thyroid-composite">
              {composite ?? "—"}
            </div>
            <div className="text-sm opacity-50">/100</div>
          </div>

          {labs?.tsh_miu_l != null && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
              <div className={`p-2 rounded-md ${isDark ? "bg-slate-800/40" : "bg-slate-50"}`} data-testid="thyroid-latest-tsh">
                <div className="text-[9px] uppercase opacity-60">TSH</div>
                <div className="text-lg font-bold tabular-nums" style={{ color: tshColor(labs.tsh_miu_l) }}>
                  {labs.tsh_miu_l} <span className="text-[10px] opacity-60">mIU/L</span>
                </div>
                <div className="text-[10px] opacity-70 mt-0.5">{bands.tsh_bg}</div>
              </div>
              {labs.ft4_pmol_l != null && (
                <div className={`p-2 rounded-md ${isDark ? "bg-slate-800/40" : "bg-slate-50"}`}>
                  <div className="text-[9px] uppercase opacity-60">FT4</div>
                  <div className="text-lg font-bold tabular-nums">{labs.ft4_pmol_l} <span className="text-[10px] opacity-60">pmol/L</span></div>
                </div>
              )}
              {labs.ft3_pmol_l != null && (
                <div className={`p-2 rounded-md ${isDark ? "bg-slate-800/40" : "bg-slate-50"}`}>
                  <div className="text-[9px] uppercase opacity-60">FT3</div>
                  <div className="text-lg font-bold tabular-nums">{labs.ft3_pmol_l} <span className="text-[10px] opacity-60">pmol/L</span></div>
                </div>
              )}
              {labs.anti_tpo_iu_ml != null && (
                <div className={`p-2 rounded-md ${isDark ? "bg-slate-800/40" : "bg-slate-50"}`} data-testid="thyroid-anti-tpo">
                  <div className="text-[9px] uppercase opacity-60">anti-TPO</div>
                  <div className="text-lg font-bold tabular-nums">{labs.anti_tpo_iu_ml} <span className="text-[10px] opacity-60">IU/mL</span></div>
                  <div className="text-[10px] opacity-70 mt-0.5">{bands.anti_tpo_bg}</div>
                </div>
              )}
              {labs.anti_tg_iu_ml != null && (
                <div className={`p-2 rounded-md ${isDark ? "bg-slate-800/40" : "bg-slate-50"}`}>
                  <div className="text-[9px] uppercase opacity-60">anti-TG</div>
                  <div className="text-lg font-bold tabular-nums">{labs.anti_tg_iu_ml} <span className="text-[10px] opacity-60">IU/mL</span></div>
                </div>
              )}
              {labs.measured_on && (
                <div className="text-[10px] opacity-60 self-end">
                  Измерено на {labs.measured_on}
                </div>
              )}
            </div>
          )}

          {/* AI Insight (mix tone) */}
          {insight && (
            <div className={`p-3 rounded-lg ${isDark ? "bg-teal-900/15 border border-teal-700/30" : "bg-teal-50 border border-teal-200"}`} data-testid="thyroid-insight">
              <div className="text-[10px] uppercase opacity-60 mb-1">✨ AI Thyroid Coach</div>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{insight}</p>
            </div>
          )}

          {!snap?.has_data && (
            <div className="p-4 text-center text-sm opacity-70" data-testid="thyroid-empty-state">
              Все още няма данни. Запиши TSH ръчно или качи лабораторно изследване по-долу.
            </div>
          )}
        </div>

        {/* TSH trend chart */}
        {trendPoints.length > 0 && (
          <div className={`p-5 rounded-2xl border mb-4 ${cardCls}`} data-testid="thyroid-trend">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase opacity-50">TSH тенденция</div>
                <div className="text-xs opacity-70">{trendPoints.length} измервания · зелена зона = 1.0-2.5 (оптимална)</div>
              </div>
              <div className="flex gap-1">
                {TREND_OPTIONS.map((o) => (
                  <button
                    key={o.id}
                    onClick={() => setTrendDays(o.id)}
                    data-testid={`thyroid-trend-${o.id}`}
                    className={`px-2 py-1 rounded-md text-[10px] font-bold transition-colors ${
                      trendDays === o.id
                        ? (isDark ? "bg-teal-500/30 text-teal-100" : "bg-teal-500 text-white")
                        : (isDark ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-700")
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <Sparkline points={trendPoints} color={isDark ? "#5eead4" : "#0d9488"} isDark={isDark} />
          </div>
        )}

        {/* Iodine + Selenium meters */}
        <div className={`p-5 rounded-2xl border mb-4 ${cardCls}`}>
          <div className="flex items-baseline justify-between mb-3">
            <div className="text-[10px] uppercase opacity-50">Микронутриенти (7-дневен среден прием)</div>
            <div className="text-[10px] opacity-60">от твоя дневник</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <NutrientMeter
              label="Йод"
              value={nutr.iodine_ug_avg}
              target={nutr.iodine_target ?? 150}
              unit="µg"
              isDark={isDark}
            />
            <NutrientMeter
              label="Селен"
              value={nutr.selenium_ug_avg}
              target={nutr.selenium_target ?? 65}
              unit="µg"
              isDark={isDark}
            />
          </div>
          {nutr.days_with_data === 0 && (
            <p className={`text-[11px] mt-2 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Запиши хранения в Дневника — оттам автоматично смятаме йода (риба, водорасли, яйца) и селена (бразилски орехи, сардини, семки).
            </p>
          )}
        </div>

        {/* Manual lab log */}
        <div className={`p-5 rounded-2xl border mb-4 ${cardCls}`} data-testid="thyroid-log-form">
          <h2 className="font-bold text-sm mb-1">Запиши лабораторни резултати</h2>
          <p className="text-[11px] opacity-70 mb-3">
            Попълни поне една стойност. Останалите остави празни — ще се актуализират при следващото изследване.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[
              { k: "tsh_miu_l",      label: "TSH (mIU/L)",        step: "0.01", testid: "thyroid-input-tsh" },
              { k: "ft4_pmol_l",     label: "FT4 (pmol/L)",       step: "0.1",  testid: "thyroid-input-ft4" },
              { k: "ft3_pmol_l",     label: "FT3 (pmol/L)",       step: "0.1",  testid: "thyroid-input-ft3" },
              { k: "anti_tpo_iu_ml", label: "anti-TPO (IU/mL)",   step: "0.1",  testid: "thyroid-input-anti-tpo" },
              { k: "anti_tg_iu_ml",  label: "anti-TG (IU/mL)",    step: "0.1",  testid: "thyroid-input-anti-tg" },
            ].map((f) => (
              <label key={f.k} className="flex flex-col gap-1">
                <span className="text-[10px] opacity-70">{f.label}</span>
                <input
                  type="number"
                  inputMode="decimal"
                  step={f.step}
                  min="0"
                  value={form[f.k]}
                  onChange={(e) => setForm((s) => ({ ...s, [f.k]: e.target.value }))}
                  data-testid={f.testid}
                  className={`px-2 py-1.5 rounded-md border text-sm ${isDark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-900"}`}
                />
              </label>
            ))}
          </div>
          <textarea
            value={form.note_bg}
            onChange={(e) => setForm((s) => ({ ...s, note_bg: e.target.value }))}
            placeholder="Бележка (по желание) — кога е изследването, лаборатория, симптоми…"
            rows="2"
            data-testid="thyroid-input-note"
            className={`w-full mt-3 px-3 py-2 rounded-md border text-sm ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300"}`}
          />
          <div className="flex flex-wrap gap-2 mt-3">
            <Button
              onClick={save}
              disabled={saving}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="thyroid-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Запиши
            </Button>
            <Link to="/dashboard/lab-twin" data-testid="thyroid-pdf-deep-link">
              <Button
                variant="outline"
                className={isDark ? "border-slate-700" : ""}
              >
                <FileText className="w-4 h-4 mr-2" />
                Качи PDF лабораторно (auto-extract)
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
